import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { CONTACTS } from "./data";

const SECTIONS = [
  {
    title: "Titolare del trattamento",
    paragraphs: [
      `Il titolare del trattamento dei dati personali è Furlan Print Copy, con sede in Viale dell'Industria 3, 20037 Paderno Dugnano (MI), P.IVA ${CONTACTS.piva}.`,
      `Puoi contattarci in qualsiasi momento: telefono ${CONTACTS.phone}, cellulare ${CONTACTS.mobile}, email ${CONTACTS.email}.`,
    ],
  },
  {
    title: "Dati che raccogliamo",
    paragraphs: [
      "Questo sito non contiene moduli di contatto né aree riservate. I dati personali vengono raccolti esclusivamente quando ci contatti di tua iniziativa tramite telefono, email o WhatsApp: in quel caso trattiamo il tuo nome, i tuoi recapiti e il contenuto della richiesta.",
      "Durante la navigazione vengono inoltre raccolti automaticamente alcuni dati tecnici in forma anonima (tipo di dispositivo, browser, pagine visitate), necessari al funzionamento e al miglioramento del sito.",
    ],
  },
  {
    title: "Finalità e base giuridica",
    paragraphs: [
      "I dati che ci fornisci volontariamente vengono utilizzati per rispondere alle tue richieste di preventivo, informazioni o assistenza (esecuzione di misure precontrattuali), e per adempiere agli obblighi di legge e fiscali in caso di rapporto commerciale.",
      "I dati tecnici anonimi di navigazione vengono utilizzati per statistiche aggregate e per migliorare il sito (legittimo interesse del titolare).",
    ],
  },
  {
    title: "Conservazione dei dati",
    paragraphs: [
      "I dati di contatto vengono conservati per il tempo necessario a gestire la tua richiesta. In caso di rapporto commerciale, i dati fiscali e contrattuali vengono conservati per i termini previsti dalla normativa vigente (10 anni).",
    ],
  },
  {
    title: "Servizi di terze parti",
    paragraphs: [
      "Il pulsante WhatsApp presente sul sito rimanda al servizio WhatsApp di Meta Platforms: la conversazione avviene secondo l'informativa privacy di quel servizio. I link social (Instagram e LinkedIn) rimandano alle rispettive piattaforme, che applicano le proprie informative.",
      "Questo sito utilizza uno strumento di analisi della navigazione in forma anonima, senza alcuna profilazione pubblicitaria.",
    ],
  },
  {
    title: "Cookie Policy",
    paragraphs: [
      "Questo sito utilizza cookie tecnici, necessari al funzionamento delle pagine, e — solo previa accettazione tramite il banner mostrato alla prima visita — strumenti di analisi della navigazione in forma anonima. Non vengono utilizzati cookie di profilazione né cookie di marketing di terze parti.",
      "Puoi accettare o rifiutare le analisi anonime dal banner cookie; per modificare la tua scelta in seguito, cancella i dati di questo sito dalle impostazioni del browser e ricarica la pagina: il banner comparirà di nuovo.",
    ],
  },
  {
    title: "I tuoi diritti",
    paragraphs: [
      `In qualsiasi momento puoi esercitare i diritti previsti dagli articoli 15-22 del Regolamento (UE) 2016/679 (GDPR): accesso, rettifica, cancellazione, limitazione del trattamento, opposizione e portabilità dei dati. È sufficiente scrivere a ${CONTACTS.email}.`,
      "Hai inoltre il diritto di proporre reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it).",
    ],
  },
];

export default function Privacy() {
  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="privacy-page" className="min-h-screen bg-[#FBF9F5]">
      <header className="px-6 sm:px-12 lg:px-20 py-8 flex items-center justify-between max-w-7xl mx-auto">
        <Link to="/" data-testid="privacy-logo-link">
          <img src="/logo.png" alt="Furlan Print & Copy" className="h-12 rounded-sm" />
        </Link>
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#6E6D69] hover:text-[#181817] transition-colors duration-300"
        >
          <ArrowLeft size={14} /> Torna al sito
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-12 pt-12 pb-24">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#C85A32]">Documento legale</p>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl font-light tracking-tight text-[#181817]">
          Privacy & Cookie Policy
        </h1>
        <p className="mt-6 text-base text-[#6E6D69] leading-relaxed">
          Informativa sul trattamento dei dati personali ai sensi del Regolamento (UE) 2016/679 (GDPR).
        </p>

        <div className="mt-14 flex flex-col gap-12">
          {SECTIONS.map((sec, i) => (
            <section key={sec.title} data-testid={`privacy-section-${i + 1}`}>
              <h2 className="font-display text-2xl font-light text-[#181817]">
                <span className="font-mono text-xs text-[#C85A32] mr-3 align-middle">{String(i + 1).padStart(2, "0")}</span>
                {sec.title}
              </h2>
              <div className="mt-4 flex flex-col gap-3 border-l border-[#E5E2DC] pl-6">
                {sec.paragraphs.map((p) => (
                  <p key={p.slice(0, 24)} className="text-sm sm:text-base text-[#181817]/75 leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <p className="mt-16 font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E6D69]">
          Ultimo aggiornamento: settembre 2026
        </p>
      </main>

      <footer className="border-t border-[#E5E2DC] px-6 sm:px-12 lg:px-20 py-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between gap-3">
          <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#6E6D69]">
            © 2026 Furlan Print Copy — P.IVA {CONTACTS.piva}
          </p>
          <a
            data-testid="privacy-website-link"
            href={CONTACTS.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#6E6D69] hover:text-[#181817] transition-colors duration-300"
          >
            {CONTACTS.website}
          </a>
        </div>
      </footer>
    </div>
  );
}
